import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AnimationSpeack from './AnimationSpeack';
import type { SessionCompleteState } from './SessionComplete';
import { useSocketMic } from './hooks/useSocketMic';
import { useLiveKitPresenter, type NarrationEvent } from './hooks/useLiveKitPresenter';
import BootLoading from './BootLoading';
import './style.css';

const IconMic = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

const IconMicOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="2" x2="22" y2="22" />
    <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
    <path d="M5 10v2a7 7 0 0 0 12 5" />
    <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

const IconPause = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M8 5.14v13.72a1 1 0 0 0 1.53.85l10.77-6.86a1 1 0 0 0 0-1.7L9.53 4.29A1 1 0 0 0 8 5.14Z" />
  </svg>
);

// The plain handset glyph, rotated 135deg via `.pms-end svg` — the conventional
// "hang up" gesture (receiver slammed into the cradle) used by most call UIs.
// The old icon here was Feather's "phone-off" (a slashed-out phone), which reads
// as "phone unavailable/muted" rather than "end this active call".
const IconEndCall = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconFullscreen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

const IconExitFullscreen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
  </svg>
);

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error';

type ShareLinkInfo = {
  valid?: boolean;
  product_id?: string;
  session_title?: string;
  presentation_duration_minutes?: number;
  default_language?: string;
  pages_count?: number;
  voice_gender?: 'MALE' | 'FEMALE' | 'NEUTRAL';
  speaking_style?: 'FORMAL' | 'CASUAL' | 'ENERGETIC' | 'CALM';
  tone?: 'FRIENDLY' | 'PROFESSIONAL' | 'PERSUASIVE' | 'EMPATHETIC';
  /** Backend keeps the presentation in `default_language`; it ignores the socket's `language` override. */
  strict_language?: boolean;
  /** Open with the mic off; the customer turns it on with the mic button. */
  start_muted?: boolean;
  /** Whose presentation this is — the header wears it instead of Nuway's own mark. */
  company_id?: string;
  company_name?: string;
  company_logo_url?: string | null;
};

type PageOut = {
  id: string;
  title: string;
  sort_order: number;
  content_path: string | null;
  content_type: 'image' | 'video' | 'pdf' | null;
  explanation: string | null;
  // Absent on the public share-link payload, which serves active pages only
  // and keeps owner-side bookkeeping out of the visitor's response.
  product_id?: string;
  is_active?: boolean;
};

type DoneAsset = {
  url: string;
  type: 'image' | 'video' | null;
};

type ActionEvent = {
  type: 'action';
  action: 'payment_link' | 'schedule_meeting' | 'nurture' | 'end_report' | string;
  status: 'executed' | 'skipped' | 'failed' | string;
  payload?: {
    payment_url?: string;
    scheduling_url?: string;
    amount?: number;
    currency?: string;
    [key: string]: unknown;
  };
};

type ReportEvent = {
  type: 'report';
  tier?: string;
  intent?: number;
  fit?: number;
  action?: string;
  action_payload?: { payment_url?: string; scheduling_url?: string; [key: string]: unknown };
  reason_summary?: string;
  turn_count?: number;
};

// ─── Runtime socket / playback constants ─────────────────────────────────────

const WS_CHANNEL = 'web';
const WS_MODE = 'product_presentation';
// Runtime-socket reconnect backoff. A dropped socket used to end the session outright —
// the customer's only recovery was reloading the page and losing the whole presentation.
const WS_RECONNECT_BASE_MS = 400;
const WS_RECONNECT_MAX_MS = 8000;
const VOICE_ENABLED = true;
// fallback only — the real value comes from the share link's voice_gender
// (POST /share-links/validate) once it loads, see the ws-connect effect below
const DEFAULT_VOICE_ACTOR = 'female';
// Small pacing pause between the narration audio finishing and requesting the next page.
const AUTO_ADVANCE_AFTER_AUDIO_MS = 600;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Must match the CSS push-transition duration (style.css `.pms-slide-layer--*`).
const SLIDE_PUSH_DURATION_MS = 520;
// With voice on, a `done` may beat its turn's audio_start (TTS still synthesizing) —
// wait at least this long before advancing without audio.
const NO_AUDIO_ADVANCE_MIN_MS = 2500;
const HEARTBEAT_INTERVAL_MS = 5000;
// The SalesPro socket carries raw PCM16 mono at 16 kHz in BOTH directions: the
// agent's narration comes down as binary frames, and the customer's microphone
// goes back up the same way. The backend bridges that to the LiveKit agent
// server-side, so the browser never talks to LiveKit itself.
const PLAYBACK_SAMPLE_RATE = 16000;
/** Jitter-buffer lead before the first chunk of a burst starts playing. */
const PLAYBACK_LEAD_SECONDS = 0.05;
// ─── Boot gate ───
// The presenter only opens once the deck has downloaded AND both sockets (our runtime
// socket and the speech one) are up. Holding the door shut matters because the server
// starts narrating the instant its socket connects — without the gate the opening line
// would play to an empty loading screen.
// A short beat after the last step turns green, so the customer actually sees it complete
// instead of the screen vanishing mid-tick.
const BOOT_SETTLE_MS = 420;
// Safety valve: if the speech socket never comes up, don't hold the presentation hostage.
// Everything still works without it (minus voice input) and the in-session pill already
// tells the customer their mic isn't reaching us.
const BOOT_AUDIO_WAIT_TIMEOUT_MS = 20000;
// Cap on server messages parked while the gate is shut. The gate normally opens in well
// under a second, so this only ever bites if something is badly wrong.
const BOOT_QUEUE_MAX = 4000;
// Standalone dead-end route the customer is sent to once the session is over.
const SESSION_COMPLETE_PATH = '/session-complete';

const readingDelayMs = (textLength: number) => Math.min(6000, Math.max(1200, textLength * 40));

// Set at build time via .env — this app is served from its own host, so there is no
// hostname to infer the environment from (and guessing wrong silently points a
// production session at the staging backend).
function getApiBase() {
  return process.env.REACT_APP_API_BASE_URL || 'https://ai.staging.nuway.co';
}

function getWsBase() {
  const override = process.env.REACT_APP_PRESENTER_WS_BASE_URL;
  if (override) return override.replace(/\/$/, '');
  const aiBase = getApiBase();
  return aiBase.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
}

function buildWsUrl(path: string, params: Record<string, string | undefined>) {
  const url = new URL(`${getWsBase()}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    url.searchParams.set(key, value);
  });
  return url.toString();
}

// ─── Visitor identity ───
// Presentation billing charges the business once per contact, and an anonymous
// visitor has no email/phone/name to key on — so the backend falls through to the
// session id, which is minted fresh on every page load. Without a durable browser-side
// id, one visitor reloading the page bills as several contacts. Opaque and random on
// purpose: this is a dedupe key, never a fingerprint.
//
// The id itself is now ONE value per browser, minted once and never rotated. The
// per-product distinction that used to come from a per-link storage key is applied at
// send time instead (see `visitorTokenFor`), so opening a second share link no longer
// mints a second identity — it reuses the same one under a different scope.
function newVisitorToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Safari < 15.4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const VISITOR_STORAGE_KEY = 'nuway.presenter.visitor';
const VISITOR_COOKIE_NAME = 'nuway_presenter_visitor';
// Long enough that it is effectively permanent. Safari's ITP caps script-written storage
// far below this regardless, which is exactly why the id lives in two places.
const VISITOR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 730;

function readVisitorCookie(): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${VISITOR_COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) || null : null;
  } catch {
    return null;
  }
}

function writeVisitorCookie(value: string) {
  try {
    // Secure only over https so local dev on plain http still stores it. Lax rather than
    // Strict because the customer usually arrives by following a link from elsewhere.
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${VISITOR_COOKIE_NAME}=${encodeURIComponent(
      value
    )}; Max-Age=${VISITOR_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Cookies disabled — localStorage may still have worked, and the in-memory cache
    // covers this page load either way.
  }
}

// Survives the case where both stores are unavailable (Safari private browsing throws on
// setItem and may refuse cookies): the id then lasts for this page load instead of being
// re-minted on every read, which is the old behaviour, not a regression.
let cachedVisitorId: string | null = null;

/**
 * The durable per-browser id. Held in localStorage *and* a cookie: read localStorage
 * first, fall back to the cookie, and mint only when neither has it. Whichever store came
 * up empty is back-filled from the other, so clearing one of the two — or one of them
 * being evicted — does not cost the visitor their identity.
 */
function getVisitorId(): string {
  if (cachedVisitorId) return cachedVisitorId;

  let fromStorage: string | null = null;
  try {
    fromStorage = window.localStorage.getItem(VISITOR_STORAGE_KEY);
  } catch {
    // Private browsing / storage disabled.
  }
  const fromCookie = readVisitorCookie();
  const id = fromStorage || fromCookie || newVisitorToken();

  if (fromStorage !== id) {
    try {
      window.localStorage.setItem(VISITOR_STORAGE_KEY, id);
    } catch {
      // Same as above — the cookie is the other half of the pair for exactly this case.
    }
  }
  if (fromCookie !== id) writeVisitorCookie(id);

  cachedVisitorId = id;
  return id;
}

// The server caps visitor_token at 64 characters, and a raw `uuid:shareToken` pair is
// about 80 — so the pair is hashed rather than concatenated. 128 bits is far more than a
// dedupe key needs, and it keeps the field short enough that the limit is not a cliff we
// are standing on. The server does not need to read the share token back out of this: it
// already has it, as `token`, in the same request body.
const VISITOR_TOKEN_HEX_CHARS = 32;

/**
 * Deterministic 128-bit hash for contexts without WebCrypto — `crypto.subtle` only exists
 * in a secure context, so plain-http local dev would otherwise have no way to derive this.
 * Not a security primitive and does not need to be: the input is already opaque and random.
 */
function fallbackHashHex(input: string): string {
  let h1 = 0x9e3779b9;
  let h2 = 0x85ebca6b;
  let h3 = 0xc2b2ae35;
  let h4 = 0x27d4eb2f;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
    h3 = Math.imul(h3 ^ c ^ (h1 >>> 15), 2246822519);
    h4 = Math.imul(h4 ^ c ^ (h2 >>> 13), 3266489917);
  }
  return [h1, h2, h3, h4].map((h) => (h >>> 0).toString(16).padStart(8, '0')).join('');
}

/**
 * What actually goes to the server: the durable browser id and the share token, hashed
 * together. The id stays fixed for the life of the browser while the share token scopes
 * it, so the same person opening two different presentations still reads as two billable
 * views — the property the old per-link storage key gave us — but a visitor who has been
 * here before is recognised on a link they have never opened.
 */
async function visitorTokenFor(shareToken: string): Promise<string> {
  const material = `${getVisitorId()}:${shareToken}`;
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, VISITOR_TOKEN_HEX_CHARS);
    }
  } catch {
    // Fall through — a browser that refuses the digest still gets a stable value below.
  }
  return fallbackHashHex(material);
}

function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function parseSocketPayload(data: MessageEvent['data']) {
  if (typeof data !== 'string') return null;
  try {
    return JSON.parse(data);
  } catch {
    return { type: 'text', content: data };
  }
}

function PresenterSlide({
  page,
  fallbackAsset,
  paused = false
}: {
  page: PageOut | null;
  fallbackAsset: DoneAsset | null;
  paused?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (paused) el.pause();
    else el.play().catch(() => {});
  }, [paused]);

  if (page?.content_path) {
    if (page.content_type === 'pdf') {
      return <iframe className="pms-document-media" src={page.content_path} title={page.title} />;
    }
    if (page.content_type === 'video') {
      return <video ref={videoRef} className="pms-document-media" src={page.content_path} autoPlay muted controls={false} loop />;
    }
    return <img className="pms-document-media" src={page.content_path} alt={page.title} />;
  }

  // No local page content — fall back to the asset the server sent with `done`.
  if (fallbackAsset?.url) {
    if (fallbackAsset.type === 'video') {
      return <video ref={videoRef} className="pms-document-media" src={fallbackAsset.url} autoPlay muted controls={false} loop />;
    }
    return <img className="pms-document-media" src={fallbackAsset.url} alt="Presentation slide" />;
  }

  if (page) {
    return (
      <div className="pms-paper-fallback">
        <div className="pms-paper-band" />
        <div className="pms-paper-body">
          <h1>{page.title}</h1>
          {page.explanation && <p>{page.explanation}</p>}
        </div>
      </div>
    );
  }

  // Nothing to draw yet — the deck is already downloaded by the time this screen mounts
  // (the boot gate waits on it), so this is the gap before the server's first `done`
  // names a slide, not a load. A blank sheet, no spinner: a "loading" affordance here
  // would be telling the customer something is pending when nothing is.
  return <div className="pms-paper-fallback" />;
}

export default function PresenterSession() {
  const { shareToken = '' } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [shareInfo, setShareInfo] = useState<ShareLinkInfo | null>(null);
  // The logo is an arbitrary uploaded URL on a public bucket. If it 404s or the
  // bucket blocks us, the header would otherwise hold an empty 24px box for the
  // whole session, so a failed load falls back to the built-in mark.
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [pages, setPages] = useState<PageOut[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [doneAsset, setDoneAsset] = useState<DoneAsset | null>(null);
  // Bumped on every rendered slide (even a re-visit of the same page index) so the
  // push-transition layers get a fresh `key` and replay each time.
  const [slideRevision, setSlideRevision] = useState(0);
  // 'forward' pushes the new slide in from the right (old exits left); 'backward'
  // is the mirror, used when the server moves to an earlier page index.
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward');
  // Snapshot of whatever was on screen right before the current slide, kept mounted
  // just long enough to animate out alongside the incoming one — a real "push"
  // needs both slides moving in lockstep, not a single fade/slide of the new one.
  const [outgoingSlide, setOutgoingSlide] = useState<{ page: PageOut | null; asset: DoneAsset | null } | null>(null);
  const outgoingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pagesRef = useRef<PageOut[]>(pages);
  const doneAssetRef = useRef<DoneAsset | null>(doneAsset);
  const [wsStatus, setWsStatus] = useState<ConnectionState>('idle');
  const [liveKitFailed, setLiveKitFailed] = useState(false);
  // LiveKit is the default transport. The socket path (with client-side noise removal,
  // see useSocketMic) is what a session drops to when LiveKit fails, and what a build
  // gets outright with REACT_APP_PRESENTER_VOICE_MODE=socket.
  const useLiveKitVoice = process.env.REACT_APP_PRESENTER_VOICE_MODE !== 'socket' && !liveKitFailed;
  // Bumping this re-runs the socket effect, which is what actually performs a retry.
  const [wsAttempt, setWsAttempt] = useState(0);
  const wsAttemptsRef = useRef(0);
  const wsRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set while the effect cleanup closes the socket on purpose, so `onclose` can tell an
  // intentional teardown apart from a real drop.
  const wsClosingRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [userMuted, setUserMuted] = useState(false);
  const [isPushToTalk, setIsPushToTalk] = useState(false);
  // Applied once, when the share link first loads — the customer's own mute toggling wins afterwards.
  const startMutedAppliedRef = useRef(false);
  useEffect(() => {
    if (!shareInfo || startMutedAppliedRef.current) return;
    startMutedAppliedRef.current = true;
    if (shareInfo.start_muted) setUserMuted(true);
  }, [shareInfo]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isAnswering, setIsAnswering] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // user-initiated "pause everything" — distinct from `isPaused` above (the server's
  // "waiting on the customer" turn state)
  const [isSessionPaused, setIsSessionPaused] = useState(false);
  const isSessionPausedRef = useRef(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [action, setAction] = useState<ActionEvent | null>(null);
  // The scheduler asked for an email address while booking a meeting: offer a
  // field for it (the PROMPT frame web voice uses). Saying it still works too.
  const [emailPrompt, setEmailPrompt] = useState<{ content: string; placeholder: string } | null>(null);
  const [emailValue, setEmailValue] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [report, setReport] = useState<ReportEvent | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [errorText, setErrorText] = useState('');

  // ── Boot gate ──
  const [dataReady, setDataReady] = useState(false);
  const [bootError, setBootError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [audioWaitTimedOut, setAudioWaitTimedOut] = useState(false);
  const [presenterReady, setPresenterReady] = useState(false);
  // Read from the socket handler, which must not re-bind every time the gate state moves.
  const isPresenterReadyRef = useRef(false);
  const pendingSocketMessagesRef = useRef<Array<ArrayBuffer | string>>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const audioTurnOpenRef = useRef(false); // between audio_start and audio_end
  const hadAudioThisTurnRef = useRef(false);
  // ── TTS playback state ──
  const playbackCtxRef = useRef<AudioContext | null>(null);
  // Every TTS source runs through this one node, so a barge-in can duck the whole
  // narration with a single ramp instead of touching each scheduled buffer.
  const playbackGainRef = useRef<GainNode | null>(null);
  const nextPlayTimeRef = useRef(0);
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  // After a barge-in we drop stale chunks of the cancelled turn until the server
  // opens the next audio turn (`audio_start`).
  const acceptTtsRef = useRef(true);
  // True once the agent has started speaking the current turn. Cleared when the
  // customer talks over it, so a `done` for the interrupted turn is recognised
  // as stale rather than advancing the deck over the answer being composed.
  const answerAudioStartedRef = useRef(true);

  // ── Auto-advance state ──
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // when a timer is live, its start time + delay — lets pauseAutoAdvance freeze it
  // with the exact remaining time instead of losing track of how far it had gotten
  const autoAdvanceMetaRef = useRef<{ startedAt: number; delayMs: number } | null>(null);
  const autoAdvanceRemainingRef = useRef<number | null>(null);
  const pendingAdvanceAfterAudioRef = useRef(false);
  const lastDonePageRef = useRef<number | null>(null);
  // Which slide is on screen. Deliberately separate from lastDonePageRef: that one
  // answers "did this turn move the deck", which drives auto-advance, while this one
  // answers "is the slide already up", which drives the push animation. A question
  // now moves the slide mid-turn, so the `done` that follows would replay a push of
  // a slide the customer has been looking at for ten seconds.
  const renderedPageRef = useRef<number | null>(null);

  // ── Turn text tracking (drives the voice-off reading-delay calc; not displayed) ──
  const turnTextRef = useRef('');
  const turnEndedRef = useRef(true);

  // ── Session lifecycle ──
  const isAnsweringRef = useRef(false);
  const endedRef = useRef(false);
  const endReportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSecondsRef = useRef(0);

  // push-to-talk effective mute: muted when (userMuted AND NOT push-to-talk)
  const effectivelyMuted = isSessionPaused || (userMuted && !isPushToTalk);

  // ─── TTS playback (raw PCM16 @16 kHz, jitter-buffered scheduling) ──────────






  const setBotPlaying = useCallback((playing: boolean) => {
    setIsAiSpeaking(playing);
    if (playing) answerAudioStartedRef.current = true;
  }, []);

  const ensurePlaybackContext = useCallback((): AudioContext | null => {
    let ctx = playbackCtxRef.current;
    if (!ctx || ctx.state === 'closed') {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext || null;
      if (!AudioCtx) return null;
      ctx = new AudioCtx({ sampleRate: PLAYBACK_SAMPLE_RATE });
      playbackCtxRef.current = ctx;
      playbackGainRef.current = null;
      nextPlayTimeRef.current = 0;
      ctx.onstatechange = () => {
        const current = playbackCtxRef.current;
        // While suspended nothing is audible, so the AI-speaking state must not
        // claim otherwise; once running, backed-up sources actually start sounding.
        setBotPlaying(current?.state === 'running' && activeSourcesRef.current.size > 0);
      };
    }
    if (!playbackGainRef.current) {
      const gain = ctx.createGain();
      gain.gain.value = 1;
      gain.connect(ctx.destination);
      playbackGainRef.current = gain;
    }
    // While the customer has paused the session the context stays suspended on
    // purpose — this is the one place new chunks would otherwise resume it.
    if (ctx.state === 'suspended' && !isSessionPausedRef.current) {
      ctx.resume().catch(() => {});
    }
    return ctx;
  }, [setBotPlaying]);

  const clearAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    autoAdvanceMetaRef.current = null;
    autoAdvanceRemainingRef.current = null;
    pendingAdvanceAfterAudioRef.current = false;
  }, []);

  const sendJson = useCallback((payload: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  /** One 20 ms PCM16 frame of the customer's microphone, straight up the socket. */
  const sendMicFrame = useCallback((frame: ArrayBuffer) => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(frame);
      return true;
    } catch {
      return false;
    }
  }, []);

  const sendNext = useCallback(() => {
    if (endedRef.current) return;
    sendJson({ type: 'next' });
  }, [sendJson]);

  const submitEmail = useCallback(() => {
    const email = emailValue.trim();
    if (!EMAIL_RE.test(email)) {
      setEmailInvalid(true);
      return;
    }
    // Sent as the customer's reply, exactly as if they had said it.
    sendJson({ type: 'message', content: email });
    setEmailPrompt(null);
  }, [emailValue, sendJson]);

  const scheduleAdvance = useCallback(
    (delayMs: number) => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceMetaRef.current = { startedAt: Date.now(), delayMs };
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        autoAdvanceMetaRef.current = null;
        // Paused — don't advance while frozen; resumeSession flushes this once the
        // user un-pauses.
        if (isSessionPausedRef.current) {
          pendingAdvanceAfterAudioRef.current = true;
          return;
        }
        // TTS may have started while this timer waited (audio_start lagging done), or
        // the turn's audio_end may have arrived with buffers still scheduled ahead on
        // the playback clock — either way defer to the audio-drain path rather than
        // advancing over the narration.
        if (audioTurnOpenRef.current || activeSourcesRef.current.size > 0) {
          pendingAdvanceAfterAudioRef.current = true;
          return;
        }
        sendNext();
      }, delayMs);
    },
    [sendNext]
  );

  // Freeze/restore the pending auto-advance timer around a pause, preserving exactly
  // how much time was left instead of restarting the wait from scratch.
  const pauseAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current && autoAdvanceMetaRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
      const { startedAt, delayMs } = autoAdvanceMetaRef.current;
      autoAdvanceRemainingRef.current = Math.max(0, delayMs - (Date.now() - startedAt));
      autoAdvanceMetaRef.current = null;
    }
  }, []);

  const resumeAutoAdvance = useCallback(() => {
    if (autoAdvanceRemainingRef.current !== null) {
      const remaining = autoAdvanceRemainingRef.current;
      autoAdvanceRemainingRef.current = null;
      scheduleAdvance(remaining);
    }
  }, [scheduleAdvance]);

  const onAudioDrained = useCallback(() => {
    if (!pendingAdvanceAfterAudioRef.current) return;
    pendingAdvanceAfterAudioRef.current = false;
    scheduleAdvance(AUTO_ADVANCE_AFTER_AUDIO_MS);
  }, [scheduleAdvance]);

  const playTtsChunk = useCallback(
    (data: ArrayBuffer) => {
      if (endedRef.current || !acceptTtsRef.current || data.byteLength < 2) return;
      const ctx = ensurePlaybackContext();
      if (!ctx) return;
      const aligned = data.byteLength % 2 === 0 ? data : data.slice(0, data.byteLength - 1);
      const int16 = new Int16Array(aligned);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768;
      }
      const buffer = ctx.createBuffer(1, float32.length, PLAYBACK_SAMPLE_RATE);
      buffer.copyToChannel(float32, 0);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(playbackGainRef.current ?? ctx.destination);
      // Scheduling each chunk on the playback clock (rather than chaining the next
      // from onended) keeps adjacent buffers gapless — a main-thread hop between
      // two of them is audible.
      const startAt = Math.max(ctx.currentTime + PLAYBACK_LEAD_SECONDS, nextPlayTimeRef.current);
      source.onended = () => {
        activeSourcesRef.current.delete(source);
        // Only clear "speaking" once the turn itself has ended (audio_end) — a brief
        // gap between two chunks mid-narration must not look like the turn finishing.
        if (activeSourcesRef.current.size === 0 && !audioTurnOpenRef.current) {
          setBotPlaying(false);
          onAudioDrained();
        }
      };
      try {
        source.start(startAt);
      } catch {
        return;
      }
      nextPlayTimeRef.current = startAt + buffer.duration;
      activeSourcesRef.current.add(source);
      // While the context is suspended (autoplay policy, pre-gesture) nothing is
      // audible; onstatechange re-engages this on resume.
      if (ctx.state === 'running') setBotPlaying(true);
    },
    [ensurePlaybackContext, onAudioDrained, setBotPlaying]
  );

  const stopPlayback = useCallback(() => {
    activeSourcesRef.current.forEach((source) => {
      source.onended = null;
      try {
        source.stop();
      } catch {}
    });
    activeSourcesRef.current.clear();
    nextPlayTimeRef.current = 0;
    audioTurnOpenRef.current = false;
    // A stop can land while the narration is ducked (the normal barge-in path) —
    // put the gain back to unity or the *next* turn plays at 10% volume.
    const gain = playbackGainRef.current;
    if (gain) {
      try {
        gain.gain.cancelScheduledValues(playbackCtxRef.current?.currentTime ?? 0);
      } catch {}
      gain.gain.value = 1;
    }
    setBotPlaying(false);
  }, [setBotPlaying]);



  // pauseSession is defined further down (it needs sendJson state that is set up
  // along the way) — this ref lets earlier callbacks reach the current version
  // without reordering the whole component, same pattern as the other *Ref
  // forwards below.
  const pauseSessionRef = useRef<() => void>(() => {});

  // ─── Voice ──────────────────────────────────────────────────────────────────
  const onLiveKitNarration = useCallback((event: NarrationEvent) => {
    if (event === 'narration_stopped') {
      audioTurnOpenRef.current = false;
      pendingAdvanceAfterAudioRef.current = false;
      clearAutoAdvance();
      setBotPlaying(false);
      return;
    }
    if (event === 'narration_start') {
      audioTurnOpenRef.current = true;
      hadAudioThisTurnRef.current = true;
      setBotPlaying(true);
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
        pendingAdvanceAfterAudioRef.current = true;
      }
      return;
    }
    audioTurnOpenRef.current = false;
    setBotPlaying(false);
    onAudioDrained();
  }, [clearAutoAdvance, onAudioDrained, setBotPlaying]);

  const liveKit = useLiveKitPresenter({
    enabled: useLiveKitVoice && !sessionEnded,
    sessionId, shareToken, isMuted: effectivelyMuted,
    onNarrationEvent: onLiveKitNarration,
    onFailure: () => setLiveKitFailed(true)
  });
  const socketMic = useSocketMic({ sendFrame: sendMicFrame, isMuted: effectivelyMuted });
  const mic = useLiveKitVoice ? liveKit : socketMic;

  // What the rest of the screen reads.
  const voiceMediaStream = mic.mediaStream;
  const voiceMicError = mic.micError;
  const voiceReady = mic.status === 'live';
  const micStart = useLiveKitVoice ? liveKit.retryMic : socketMic.start;
  const micStop = useLiveKitVoice ? liveKit.stop : socketMic.stop;

  /** The mic-error banner's "Try again". */
  const retryVoiceLeg = useCallback(() => {
    micStop();
    void micStart();
  }, [micStart, micStop]);

  const currentPage = pages[currentPageIndex] ?? null;
  // pages_count from /share-links/validate is the authoritative total — the pages list
  // fetch below can fail for this unauthenticated customer session, leaving pages empty
  const totalPages = shareInfo?.pages_count ?? pages.length;
  const sessionTitle = shareInfo?.session_title || 'Presentation';

  // The presentation is the company's, so the header carries their name and logo.
  // Nuway's own mark is the fallback for links whose validate response predates
  // these fields, and for the catch path below that fabricates a minimal shareInfo
  // when the request itself fails.
  const brandName = shareInfo?.company_name || 'NUWAY';
  const brandLogoUrl = shareInfo?.company_logo_url || '';

  const progress = useMemo(() => {
    if (!totalPages) return 0;
    return Math.min(100, Math.max(0, ((currentPageIndex + 1) / totalPages) * 100));
  }, [currentPageIndex, totalPages]);

  // handleDone is a long-lived callback (stable deps) that reads `pages`/`doneAsset`
  // only through refs — mirrored here so it always sees the latest values instead of
  // whatever was current when the callback was first created.
  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);
  useEffect(() => {
    doneAssetRef.current = doneAsset;
  }, [doneAsset]);

  // The clock is the customer's sense of how long they have been in the presentation —
  // it must not count the boot wait.
  useEffect(() => {
    if (isSessionPaused || !presenterReady) return undefined;
    const timer = window.setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isSessionPaused, presenterReady]);

  // ─── Boot gate ─────────────────────────────────────────────────────────────

  const bootDataDone = dataReady;
  const bootLinkDone = wsStatus === 'connected';
  // A denied/absent microphone is a settled outcome, not something more waiting will fix,
  // and the presentation is still worth watching without voice input — so it clears the
  // voice step rather than trapping the customer on the boot screen.
  const bootVoiceDone = voiceReady || !!voiceMicError || audioWaitTimedOut;
  // The asset download is the only step with real sub-progress (it is weighted by file
  // size), so the bar moves continuously through it and then steps once per stage.
  const bootSteps = [
    { key: 'data', label: 'Preparing your session', done: bootDataDone, fraction: bootDataDone ? 1 : 0 },
    { key: 'link', label: 'Connecting to the server', done: bootLinkDone, fraction: bootLinkDone ? 1 : 0 },
    { key: 'voice', label: 'Finishing up', done: bootVoiceDone, fraction: bootVoiceDone ? 1 : 0 }
  ];
  const bootProgress = Math.round((bootSteps.reduce((sum, step) => sum + step.fraction, 0) / bootSteps.length) * 100);
  const allBootStepsDone = bootSteps.every((step) => step.done);

  useEffect(() => {
    if (presenterReady || !bootDataDone || !bootLinkDone || bootVoiceDone) return undefined;
    const timer = setTimeout(() => setAudioWaitTimedOut(true), BOOT_AUDIO_WAIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [bootVoiceDone, bootDataDone, bootLinkDone, presenterReady]);

  useEffect(() => {
    if (presenterReady || !allBootStepsDone) return undefined;
    const timer = setTimeout(() => {
      // The ref flips first: the socket handler reads it synchronously and must stop
      // queueing before the drain effect runs, or a message could land in both paths.
      isPresenterReadyRef.current = true;
      setPresenterReady(true);
      setElapsed(0);
    }, BOOT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [allBootStepsDone, presenterReady]);

  const retryBoot = () => {
    if (bootError && !bootError.retryable) return;
    setBootError(null);
    setBootAttempt((n) => n + 1);
  };

  // ─── Validate share link and start session ─────────────────────────────────

  useEffect(() => {
    let active = true;
    async function startSession() {
      if (!shareToken) return;
      try {
        const validateResponse = await fetch(`${getApiBase()}/api/v1/sales/share-links/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: shareToken, password: null })
        });
        const nextShareInfo = await validateResponse.json();
        if (!active) return;
        setShareInfo(nextShareInfo);
        if (nextShareInfo?.valid === false) {
          // Terminal — retrying a revoked/expired link will never succeed, so the boot
          // screen says so instead of spinning forever.
          setBootError({ message: 'This presentation link is no longer available.', retryable: false });
          return;
        }

        // Derived before the requests go out because hashing is async — it is a few
        // microseconds of work, not a round trip, so the two fetches below still start
        // together.
        const visitorToken = await visitorTokenFor(shareToken);
        if (!active) return;

        // fetch pages and start session in parallel
        const [startResponse, pagesResponse] = await Promise.all([
          fetch(`${getApiBase()}/api/v1/sales/sessions/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // visitor_token is deliberately sent here and nowhere else. The socket
            // reads it back off the CustomerSession row, so it stays
            // server-authoritative with a single entry point into the system.
            body: JSON.stringify({ token: shareToken, visitor_token: visitorToken })
          }),
          // The visitor holds no bearer token, so the owner-authenticated
          // products route always answered 401 and the deck never loaded. The
          // share token is the credential for the slides behind this link.
          fetch(`${getApiBase()}/api/v1/sales/share-links/${shareToken}/pages`)
        ]);

        if (!active) return;

        const session = await startResponse.json();
        const nextSessionId = session.id || session.session_id || '';
        // Without a session id there is no socket to open, so the gate would never
        // clear — surface it as a retryable failure rather than an endless spinner.
        if (!nextSessionId) throw new Error('session start returned no id');
        setSessionId(nextSessionId);

        if (pagesResponse?.ok) {
          const pagesData: PageOut[] = await pagesResponse.json();
          // The public payload is already scoped to active pages and omits the flag.
          const sorted = [...pagesData].filter((p) => p.is_active !== false).sort((a, b) => a.sort_order - b.sort_order);
          setPages(sorted);
          setCurrentPageIndex(0);
        }
        setDataReady(true);
      } catch {
        if (!active) return;
        setShareInfo((prev) => prev ?? { valid: true, session_title: 'Presentation' });
        setBootError({ message: "We couldn't reach the server.", retryable: true });
      }
    }
    startSession();
    return () => {
      active = false;
    };
  }, [shareToken, bootAttempt]);

  // ─── Runtime socket message handling ────────────────────────────────────────

  const finishSession = useCallback(
    (reportEvent: ReportEvent | null) => {
      if (endReportTimeoutRef.current) {
        clearTimeout(endReportTimeoutRef.current);
        endReportTimeoutRef.current = null;
      }
      endedRef.current = true;
      clearAutoAdvance();
      stopPlayback();
      micStop();
      if (reportEvent) setReport(reportEvent);
      isAnsweringRef.current = false;
      setIsAnswering(false);
      setSessionEnded(true);
    },
    [clearAutoAdvance, micStop, stopPlayback]
  );

  // Routed through a ref so the socket effect does not reconnect every time
  // finishSession's identity changes.
  const finishSessionRef = useRef(finishSession);
  useEffect(() => {
    finishSessionRef.current = finishSession;
  }, [finishSession]);

  // Put a slide on screen, pushing the old one out. Shared by `done` (the deck moved
  // itself) and `page` (a question pulled it somewhere), because "show this slide" is
  // one behaviour and it looked like two.
  const showPage = useCallback((page: number, asset: unknown, assetType: unknown) => {
    const rendered = renderedPageRef.current;
    if (rendered === page) {
      // Already up — refresh the asset without animating a push to where we are.
      setDoneAsset(asset ? { url: asset as string, type: (assetType as DoneAsset['type']) ?? null } : null);
      return;
    }
    // Direction mirrors the navigation: moving to a later page pushes the new slide in
    // from the right (old exits left); an earlier page — a "go back" command, or a
    // question about a slide already shown — reverses it.
    const direction: 'forward' | 'backward' = rendered !== null && page < rendered ? 'backward' : 'forward';
    if (rendered !== null) {
      // Snapshot what's currently on screen so it can push out while the new slide
      // pushes in — a real push needs both layers moving in lockstep.
      if (outgoingTimeoutRef.current) clearTimeout(outgoingTimeoutRef.current);
      setOutgoingSlide({ page: pagesRef.current[rendered] ?? null, asset: doneAssetRef.current });
      outgoingTimeoutRef.current = setTimeout(() => setOutgoingSlide(null), SLIDE_PUSH_DURATION_MS);
    } else {
      setOutgoingSlide(null);
    }
    setSlideDirection(direction);
    setCurrentPageIndex(page);
    setDoneAsset(asset ? { url: asset as string, type: (assetType as DoneAsset['type']) ?? null } : null);
    setSlideRevision((v) => v + 1);
    renderedPageRef.current = page;
  }, []);

  // The server moved the deck to the slide a question is about, or moved it back when
  // the walkthrough resumed. It arrives ahead of the answer it belongs to, so the
  // customer is looking at the right slide while it is spoken — `done` would only
  // arrive once the answer had finished.
  const handlePage = useCallback(
    (event: any) => {
      if (typeof event.page !== 'number') return;
      showPage(event.page, event.asset, event.asset_type);
    },
    [showPage]
  );

  const handleDone = useCallback(
    (event: any) => {
      clearAutoAdvance();
      turnEndedRef.current = true;

      const page = typeof event.page === 'number' ? event.page : null;
      const prevPageIndex = lastDonePageRef.current;
      if (page !== null) showPage(page, event.asset, event.asset_type);
      const paused = event.paused === true;
      setIsPaused(paused);
      if (page !== null) lastDonePageRef.current = page;

      // After the customer talks over the narration, the interrupted turn's `done`
      // can still arrive (it was already on the wire, or the server finished the turn
      // before seeing the interruption). Until the agent starts speaking the answer,
      // such a `done` is stale: sync the rendered page but keep Answering and don't
      // advance — otherwise a 600 ms advance timer fires while the answer is still
      // being composed.
      const isAnswerTurn =
        event.kind === 'question' || event.kind === 'objection' || event.kind === 'buying' || event.kind === 'disengaging';
      if (isAnsweringRef.current && !answerAudioStartedRef.current && !isAnswerTurn) {
        hadAudioThisTurnRef.current = false;
        return;
      }
      isAnsweringRef.current = false;
      setIsAnswering(false);

      // Auto-advance: plain narration walks itself forward, questions/objections wait
      // for the customer.
      //
      // `stage` is what says whether the deck is still running — NOT the page number.
      // Staging sends several narration turns per slide (verified: three `done`s with
      // page 0, then two with page 1), so the old "page did not change ⇒ last page"
      // test stopped the whole presentation dead after the very first turn. The deck
      // ends by flipping stage to `answering` on the wrap-up turn ("That covers
      // everything… what questions do you have for me?"), after which we must stop and
      // listen instead of sending `next` — keep sending and the agent just loops.
      const eligible = event.kind === 'none' || (event.kind === 'navigation' && event.paused === false);

      if (!endedRef.current && eligible && !paused && event.stage === 'presenting') {
        const audioStillPlaying = audioTurnOpenRef.current || activeSourcesRef.current.size > 0;
        if (audioStillPlaying || isSessionPausedRef.current) {
          // Advance once local playback fully drains (or, if paused, once resumeSession flushes it).
          pendingAdvanceAfterAudioRef.current = true;
        } else if (hadAudioThisTurnRef.current) {
          scheduleAdvance(AUTO_ADVANCE_AFTER_AUDIO_MS);
        } else {
          // No TTS started for this turn yet — voice is on, so audio is probably still
          // being synthesized. Wait a generous window (the scheduled callback re-checks
          // and defers to the drain path if audio appears meanwhile).
          scheduleAdvance(Math.max(readingDelayMs(turnTextRef.current.length), VOICE_ENABLED ? NO_AUDIO_ADVANCE_MIN_MS : 0));
        }
      }
      hadAudioThisTurnRef.current = false;
    },
    [clearAutoAdvance, scheduleAdvance, showPage]
  );

  // Pulled out of the socket's `onmessage` so the boot gate can park messages and replay
  // them through the exact same path once the presenter is on screen.
  const handleSocketMessage = useCallback(
    (data: ArrayBuffer | string) => {
      // The narration itself: raw PCM16 @ 16 kHz.
      if (data instanceof ArrayBuffer) {
        playTtsChunk(data);
        return;
      }
      const payload = parseSocketPayload(data);
      if (!payload) return;
      switch (payload.type) {
        case 'chunk': {
          if (!payload.content) break;
          if (turnEndedRef.current) {
            turnEndedRef.current = false;
            turnTextRef.current = payload.content;
          } else {
            turnTextRef.current += payload.content;
          }
          break;
        }
        case 'score':
          // Live lead scoring is internal — this screen faces the customer, so we
          // deliberately do not render intent/fit/tier here.
          break;
        case 'action': {
          const actionEvent = payload as ActionEvent;
          if (actionEvent.status === 'executed' && (actionEvent.payload?.payment_url || actionEvent.payload?.scheduling_url)) {
            setAction(actionEvent);
          }
          break;
        }
        case 'speech_detected': {
          // Server-side barge-in: the turn is already cancelled upstream, so stop
          // the narration we have buffered and drop the rest of it.
          stopPlayback();
          clearAutoAdvance();
          acceptTtsRef.current = false;
          break;
        }
        case 'audio_start': {
          audioTurnOpenRef.current = true;
          hadAudioThisTurnRef.current = true;
          acceptTtsRef.current = true;
          // Mark the turn as started as soon as it is accepted, not only once the
          // first chunk is scheduled: TTS can lag `audio_start` by a beat, and a
          // customer who talks in that gap must still count as interrupting.
          setBotPlaying(true);
          // A reading-delay advance already scheduled for this turn defers to the
          // audio-drain path instead.
          if (autoAdvanceTimerRef.current) {
            clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = null;
            pendingAdvanceAfterAudioRef.current = true;
          }
          break;
        }
        case 'audio_end': {
          // LiveKit's worker reports the real playout end over the room. This
          // socket marker only says text generation has finished.
          if (useLiveKitVoice) break;
          audioTurnOpenRef.current = false;
          if (activeSourcesRef.current.size === 0) {
            setBotPlaying(false);
            onAudioDrained();
          }
          break;
        }
        case 'stt_transcript':
          // Informational — the backend owns STT now (it bridges our mic audio to
          // our mic audio to the agent server-side).
          break;
        case 'deck': {
          // The slide list, delivered over the room instead of fetched from REST.
          // Normalised exactly like the REST payload was, so everything
          // downstream (image loading, page lookup on `done`) is unchanged.
          const rows: PageOut[] = Array.isArray((payload as any).pages) ? (payload as any).pages : [];
          if (!rows.length) break;
          const sorted = [...rows].filter((p) => p.is_active !== false).sort((a, b) => a.sort_order - b.sort_order);
          setPages(sorted);
          setDataReady(true);
          break;
        }
        case 'page':
          handlePage(payload);
          break;
        case 'done':
          handleDone(payload);
          break;
        case 'session_ending':
          // The customer said goodbye and it has played: show the end screen
          // now. The report follows once the server has finalized the session
          // (10-17 s on production) and only adds its call to action.
          finishSessionRef.current(null);
          break;
        case 'report':
          finishSessionRef.current(payload as ReportEvent);
          break;
        case 'error':
          if (payload.detail) setErrorText(String(payload.detail));
          break;
        case 'PROMPT': {
          const prompt = payload as any;
          const field = prompt.inputField;
          if (field?.enabled && field?.dataType === 'email') {
            setEmailPrompt({
              content: String(prompt.content || ''),
              placeholder: String(field.placeholder || 'Enter your email address'),
            });
            setEmailValue('');
            setEmailInvalid(false);
          }
          break;
        }
        default:
          break;
      }
    },
    [clearAutoAdvance, handleDone, handlePage, onAudioDrained, playTtsChunk, setBotPlaying, stopPlayback, useLiveKitVoice]
  );

  /**
   * The one way in for control messages, whichever transport carried them.
   *
   * The server opens its first narration turn the moment the session comes up,
   * which is *before* the boot gate lets the customer through. Park those and
   * replay them on entry, so the presentation starts from its first word instead
   * of dropping the opening into a loading screen. Still queueing while a drain
   * is outstanding, too: the ref flips a beat before the drain effect runs, and a
   * message that slipped through in between would be handled ahead of everything
   * already parked.
   */
  const ingestControlMessage = useCallback(
    (data: ArrayBuffer | string) => {
      const queue = pendingSocketMessagesRef.current;
      if (!isPresenterReadyRef.current || queue.length > 0) {
        queue.push(data);
        while (queue.length > BOOT_QUEUE_MAX) queue.shift();
        return;
      }
      handleSocketMessage(data);
    },
    [handleSocketMessage]
  );

  // Once the gate opens, replay everything the server said while it was shut, in order.
  useEffect(() => {
    if (!presenterReady) return;
    const queued = pendingSocketMessagesRef.current;
    if (!queued.length) return;
    pendingSocketMessagesRef.current = [];
    queued.forEach((data) => handleSocketMessage(data));
  }, [presenterReady, handleSocketMessage]);

  useEffect(() => {
    if (!sessionId || !shareToken) return undefined;
    // Keep "reconnecting" on screen across the retry itself — dropping back to
    // "connecting" would flicker the banner off and on between attempts.
    setWsStatus((prev) => (prev === 'reconnecting' ? prev : 'connecting'));
    // Voice/language config comes from the share link (POST /share-links/validate) —
    // it used to be hardcoded here (always "female", no language/style/tone at all),
    // so a share link's actual voice_gender/speaking_style/tone/default_language were
    // silently ignored once the session actually connected.
    const socket = new WebSocket(
      buildWsUrl(`/api/v1/sales/pro/ws/${sessionId}`, {
        token: shareToken,
        channel: WS_CHANNEL,
        mode: WS_MODE,
        // Voice on: the runtime socket streams narration and microphone PCM.
        voice: useLiveKitVoice ? 'livekit' : '1',
        voice_actor: (shareInfo?.voice_gender ?? DEFAULT_VOICE_ACTOR.toUpperCase()).toLowerCase(),
        language: shareInfo?.default_language ?? 'en',
        speaking_style: shareInfo?.speaking_style ?? 'CASUAL',
        tone: shareInfo?.tone ?? 'FRIENDLY'
      })
    );
    socket.binaryType = 'arraybuffer';
    socketRef.current = socket;

    socket.onopen = () => {
      wsAttemptsRef.current = 0;
      setWsStatus('connected');
    };
    socket.onerror = () => setWsStatus((prev) => (prev === 'connected' ? prev : 'error'));
    socket.onclose = () => {
      // Deliberate teardown (session finished / effect cleanup) must stay closed; anything
      // else is an outage the customer shouldn't have to recover from by reloading.
      if (endedRef.current || wsClosingRef.current) {
        setWsStatus('closed');
        return;
      }
      setWsStatus('reconnecting');
      wsAttemptsRef.current = Math.min(wsAttemptsRef.current + 1, 8);
      const delay = Math.min(WS_RECONNECT_BASE_MS * 2 ** (wsAttemptsRef.current - 1), WS_RECONNECT_MAX_MS);
      if (wsRetryTimerRef.current) clearTimeout(wsRetryTimerRef.current);
      wsRetryTimerRef.current = setTimeout(() => {
        wsRetryTimerRef.current = null;
        if (!endedRef.current) setWsAttempt((n) => n + 1);
      }, delay);
    };
    socket.onmessage = (event) => {
      ingestControlMessage(event.data as ArrayBuffer | string);
    };

    return () => {
      wsClosingRef.current = true;
      socket.onmessage = null;
      socket.onopen = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
      socketRef.current = null;
      wsClosingRef.current = false;
      clearAutoAdvance();
    };
    // `wsAttempt` is the retry trigger: incrementing it tears the dead socket down and
    // builds a fresh one through this same path.
  }, [sessionId, shareToken, shareInfo, wsAttempt, ingestControlMessage, clearAutoAdvance, useLiveKitVoice]);

  // Don't leave a retry armed after the session ends or the page unmounts.
  useEffect(
    () => () => {
      if (wsRetryTimerRef.current) clearTimeout(wsRetryTimerRef.current);
    },
    []
  );

  // ─── Engagement heartbeat (~5 s, cumulative foreground seconds) ─────────────

  useEffect(() => {
    // Time spent on the boot screen isn't engagement — start counting at the presenter.
    if (wsStatus !== 'connected' || isSessionPaused || !presenterReady) return undefined;
    const secondsTimer = window.setInterval(() => {
      if (window.document.visibilityState === 'visible') {
        activeSecondsRef.current += 1;
      }
    }, 1000);
    const heartbeatTimer = window.setInterval(() => {
      if (!endedRef.current) {
        sendJson({ type: 'engagement', active_sec: activeSecondsRef.current });
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => {
      window.clearInterval(secondsTimer);
      window.clearInterval(heartbeatTimer);
    };
  }, [wsStatus, sendJson, isSessionPaused, presenterReady]);

  // ─── Voice: join the room once the session exists ──────────────────────────
  useEffect(() => {
    if (!sessionId || useLiveKitVoice) return undefined;
    // The banner surfaces the reason; there is no other transport to fall back
    // to, so a failure here leaves the presentation watchable but silent.
    void micStart();
    return () => {
      micStop();
    };
  }, [sessionId, micStart, micStop, useLiveKitVoice]);

  // ─── Push-to-talk (Space) — only when muted ────────────────────────────────

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || !userMuted) return;
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      e.preventDefault();
      setIsPushToTalk(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      e.preventDefault();
      setIsPushToTalk(false);
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [userMuted]);

  // ─── Fullscreen ────────────────────────────────────────────────────────────

  useEffect(() => {
    const onChange = () => {
      const entering = !!window.document.fullscreenElement;
      setIsFullscreen(entering);
      if (!entering) {
        setControlsVisible(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      }
    };
    window.document.addEventListener('fullscreenchange', onChange);
    return () => window.document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (!window.document.fullscreenElement) {
      window.document.documentElement.requestFullscreen().catch(() => {});
    } else {
      window.document.exitFullscreen().catch(() => {});
    }
  };

  const handleMouseMove = () => {
    if (!isFullscreen) return;
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 3000);
  };

  // ─── Pause/resume: a client-side freeze — mic, TTS playback, the elapsed clock,
  // and the auto-advance timer all halt together, then pick back up from the exact
  // same spot — by suspending the Web Audio clock itself, any narration already in
  // flight freezes mid-buffer instead of being discarded, and continues on resume
  // rather than restarting. The server is also told via a plain "pause"/"resume"
  // message so it can hold its own turn state in sync. ──

  const pauseSession = () => {
    if (endedRef.current || isSessionPausedRef.current) return;
    isSessionPausedRef.current = true;
    setIsSessionPaused(true);
    pauseAutoAdvance();
    // Pausing mutes the mic through `effectivelyMuted` and tells the server to
    // hold the turn; the agent's own audio is the server's to stop.
    sendJson({ type: 'message', content: 'pause' });
  };
  // keep the ref pointing at the current closure (fresh every render, same as
  // the other *Ref forwards in this file)
  pauseSessionRef.current = pauseSession;

  const resumeSession = () => {
    if (endedRef.current || !isSessionPausedRef.current) return;
    isSessionPausedRef.current = false;
    setIsSessionPaused(false);
    sendJson({ type: 'message', content: 'resume' });
    resumeAutoAdvance();
    if (pendingAdvanceAfterAudioRef.current && !audioTurnOpenRef.current && activeSourcesRef.current.size === 0) {
      onAudioDrained();
    }
  };

  // ─── End session: ask the server to finalize, show the end screen at once ──

  const endSession = () => {
    if (endedRef.current) return;
    isSessionPausedRef.current = false;
    setIsSessionPaused(false);
    sendJson({ type: 'end' });
    // Gate the whole protocol immediately: in-flight TTS frames and the in-flight
    // turn's `done` keep arriving while the server finalizes — without this the
    // narration audibly resumes and auto-advance can send `next` after `end`.
    // The end screen shows now rather than after the report (it waited up to
    // five seconds); the report still lands on it with its call to action.
    finishSession(null);
  };

  useEffect(
    () => () => {
      if (endReportTimeoutRef.current) clearTimeout(endReportTimeoutRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (outgoingTimeoutRef.current) clearTimeout(outgoingTimeoutRef.current);
    },
    []
  );

  const isLive = wsStatus === 'connected' && !sessionEnded;
  // Either socket being down means the customer's voice can't reach the agent right now.
  // Rather than explain that in a paragraph, the mic itself goes offline-looking and
  // disabled — the state is shown where the customer would otherwise try to act on it.
  const runtimeDown = !sessionEnded && (wsStatus === 'reconnecting' || wsStatus === 'error');
  const sttDown = !sessionEnded && mic.status === 'starting';
  const connectionLost = runtimeDown || sttDown;

  // Orb states: 'client' = user speaking (blue), 'bot' = AI narrating (purple),
  // 'think' = processing (gray pulses), 'idle' = calm small sphere.
  // The server owns turn detection now, so there is no local "the customer is
  // speaking" verdict to read. The orb draws its level straight off the live mic
  // track instead (AnimationSpeack builds its own analyser when no rmsLevel is
  // given), and 'client' simply means "your mic is open".
  const userDisplayMode: 'idle' | 'think' | 'client' | 'bot' = effectivelyMuted
    ? 'idle'
    : mic.status === 'starting'
      ? 'think'
      : mic.status === 'live'
        ? 'client'
        : 'idle';
  const aiDisplayMode: 'idle' | 'think' | 'bot' = isAiSpeaking ? 'bot' : isLive ? 'think' : 'idle';
  // The fullscreen mini-orb (top-right) has no separate "You" tile to fall back on,
  // so it represents BOTH speakers: purple while the AI narrates, blue while the
  // customer talks, and the same idle/think look otherwise. Normal (non-fullscreen)
  // mode keeps its own two separate orbs and is unaffected.
  const fsOrbStatus: 'idle' | 'think' | 'client' | 'bot' =
    aiDisplayMode === 'bot' ? 'bot' : userDisplayMode === 'client' ? 'client' : aiDisplayMode;

  const actionUrl = action?.payload?.payment_url || action?.payload?.scheduling_url || '';
  const actionLabel = action?.payload?.payment_url ? 'Pay now' : 'Book a call';
  const reportActionUrl = report?.action_payload?.payment_url || report?.action_payload?.scheduling_url || '';
  const reportActionLabel = report?.action_payload?.payment_url ? 'Pay now' : 'Book a call';

  // Leaving the session route unmounts this component, which tears down the socket,
  // the mic and the audio context through the existing cleanup effects — the customer
  // lands on a standalone thank-you page with nothing left running behind it.
  // `replace` so the browser back button can't drop them into a dead session.
  const leaveToThankYou = (feedbackSubmitted: boolean) => {
    const state: SessionCompleteState = {
      sessionTitle,
      feedbackSubmitted,
      actionUrl: reportActionUrl || actionUrl || undefined,
      actionLabel: reportActionUrl ? reportActionLabel : actionUrl ? actionLabel : undefined
    };
    navigate(SESSION_COMPLETE_PATH, { replace: true, state });
  };

  const submitFeedback = async () => {
    if (feedbackRating === 0 || feedbackStatus === 'submitting') return;
    setFeedbackStatus('submitting');
    try {
      const res = await fetch(`${getApiBase()}/api/v1/sales/sessions/${sessionId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: feedbackRating, comment: feedbackComment.trim() || undefined })
      });
      if (!res.ok) throw new Error(`Feedback submit failed: ${res.status}`);
      setFeedbackStatus('submitted');
      leaveToThankYou(true);
    } catch {
      // Stay on the card so the rating isn't silently lost — the button offers a retry.
      setFeedbackStatus('error');
    }
  };

  // One screen holds the whole startup: assets download, then the session data, then both
  // websockets. Every hook above still runs behind it — the deck keeps downloading, the
  // sockets keep connecting, the server's opening turn keeps being parked — only the
  // presenter itself is withheld until all four steps have landed.
  if (!presenterReady) {
    return <BootLoading steps={bootSteps} progress={bootProgress} error={bootError} onRetry={retryBoot} />;
  }

  return (
    <main
      className={`pms-page${isFullscreen ? ' is-fullscreen' : ''}${isFullscreen && !controlsVisible ? ' pms-nocursor' : ''}`}
      onMouseMove={handleMouseMove}
    >
      <header className="pms-header">
        <div className="pms-brand">
          {brandLogoUrl && !brandLogoFailed ? (
            // No alt text: the company name sits right beside it, so describing the
            // logo again would only repeat what the next element already says.
            <img className="pms-logo pms-logo-img" src={brandLogoUrl} alt="" onError={() => setBrandLogoFailed(true)} />
          ) : (
            <span className="pms-logo"> </span>
          )}
          <strong>{brandName}</strong>
          <span>· {sessionTitle}</span>
        </div>
        <div className="pms-header-meta">
          <span>
            {String(currentPageIndex + 1).padStart(2, '0')}
            {totalPages > 0 && ` / ${String(totalPages).padStart(2, '0')}`}
          </span>
          {totalPages > 0 && (
            <div className="pms-progress">
              <i style={{ width: `${progress}%` }} />
            </div>
          )}
          <b />
          <span className={isLive ? 'pms-live-dot' : 'pms-idle-dot'} />
          <strong>{formatTime(elapsed)}</strong>
        </div>
      </header>

      <section className="pms-stage">
        <div className="pms-document">
          <div className="pms-slide-track">
            {outgoingSlide && (
              <div key={`out-${slideRevision}`} className={`pms-slide-layer pms-slide-layer--outgoing pms-slide-layer--${slideDirection}`}>
                <PresenterSlide page={outgoingSlide.page} fallbackAsset={outgoingSlide.asset} paused={isSessionPaused} />
              </div>
            )}
            <div key={`in-${slideRevision}`} className={`pms-slide-layer pms-slide-layer--incoming pms-slide-layer--${slideDirection}`}>
              <PresenterSlide page={currentPage} fallbackAsset={doneAsset} paused={isSessionPaused} />
            </div>
          </div>
        </div>

        <aside className="pms-people" aria-label="Session participants">
          <div className="pms-person pms-presenter">
            <span className="pms-kicker">Presenter</span>
            <div className="pms-speack-visual" aria-hidden="true">
              <div className={`pms-orb-shell pms-orb-shell--${aiDisplayMode}`}>
                <AnimationSpeack mediaStream={null} status={aiDisplayMode} isMuted={false} />
              </div>
            </div>
            <div className="pms-person-footer">
              <span className={isAiSpeaking ? 'pms-online-dot' : 'pms-idle-dot'} />
              <strong>Alex</strong>
              <span>· AI</span>
            </div>
            <div className="pms-eq" aria-hidden="true">
              {[6, 10, 7, 14, 9, 12, 7].map((height, index) => (
                <i key={index} style={{ height }} />
              ))}
            </div>
          </div>
          <div className="pms-person pms-you">
            <span className="pms-kicker">You</span>
            <div className="pms-user-visual" aria-hidden="true">
              <div className={`pms-orb-shell pms-orb-shell--${userDisplayMode}`}>
                <AnimationSpeack
                  mediaStream={effectivelyMuted ? null : voiceMediaStream}
                  status={userDisplayMode}
                  isMuted={effectivelyMuted}
                  rmsLevel={undefined}
                />
              </div>
            </div>
            <div className="pms-person-footer">
              <span className={effectivelyMuted || mic.status !== 'live' ? 'pms-idle-dot' : 'pms-online-dot'} />
              <strong>You</strong>
              {effectivelyMuted && <span>· Muted</span>}
              {!effectivelyMuted && mic.status === 'live' && <span>· Live</span>}
            </div>
          </div>
        </aside>
      </section>

      {isFullscreen && (
        <div className="pms-fs-timer" aria-hidden="true">
          <span className={isLive ? 'pms-live-dot' : 'pms-idle-dot'} />
          <strong>{formatTime(elapsed)}</strong>
        </div>
      )}

      {isFullscreen && (
        <div className="pms-fs-alex" aria-hidden="true">
          <AnimationSpeack
            mediaStream={fsOrbStatus === 'client' && !effectivelyMuted ? voiceMediaStream : null}
            status={fsOrbStatus}
            isMuted={fsOrbStatus === 'client' ? effectivelyMuted : false}
            rmsLevel={undefined}
          />
        </div>
      )}

      {errorText && (
        <div className="pms-error-banner" role="alert">
          ⚠ {errorText}
        </div>
      )}

      {voiceMicError && !sessionEnded && (
        <div className="pms-error-banner pms-mic-error-banner" role="alert">
          <span>🎙 {voiceMicError}</span>
          <button type="button" onClick={() => retryVoiceLeg()}>
            Try again
          </button>
        </div>
      )}

      {useLiveKitVoice && liveKit.audioBlocked && !sessionEnded && (
        <div className="pms-error-banner pms-mic-error-banner" role="alert">
          <span>Sound is paused by your browser.</span>
          <button type="button" onClick={() => void liveKit.startAudio()}>
            Enable sound
          </button>
        </div>
      )}

      {connectionLost && !voiceMicError && (
        <div className="pms-reconnect-pill" role="status" aria-live="polite">
          <span className="pms-reconnect-orbit" aria-hidden="true" />
          <span>Reconnecting</span>
          <span className="pms-reconnect-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </div>
      )}

      {action && actionUrl && !sessionEnded && (
        <div className="pms-action-banner" role="dialog" aria-label="Next step">
          <span>{action.action === 'payment_link' ? 'Ready to check out?' : 'Want to talk to a specialist?'}</span>
          <a href={actionUrl} target="_blank" rel="noreferrer">
            {actionLabel}
          </a>
          <button type="button" aria-label="Dismiss" onClick={() => setAction(null)}>
            ×
          </button>
        </div>
      )}

      {emailPrompt && !sessionEnded && (
        <form
          className="pms-email-prompt"
          role="dialog"
          aria-label="Your email address"
          onSubmit={(e) => {
            e.preventDefault();
            submitEmail();
          }}
        >
          {emailPrompt.content && <span className="pms-email-prompt-text">{emailPrompt.content}</span>}
          <div className="pms-email-prompt-row">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder={emailPrompt.placeholder}
              value={emailValue}
              aria-invalid={emailInvalid || undefined}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailInvalid(false);
              }}
            />
            <button type="submit" className="pms-email-prompt-send">
              Send
            </button>
            <button type="button" className="pms-email-prompt-close" aria-label="Dismiss" onClick={() => setEmailPrompt(null)}>
              ×
            </button>
          </div>
          {emailInvalid && <span className="pms-email-prompt-error">Please enter a valid email address.</span>}
        </form>
      )}

      <div className={`pms-controls${isFullscreen && !controlsVisible ? ' controls-hidden' : ''}`}>
        {!sessionEnded && (
          <>
            <div className={`pms-ctrl-group${connectionLost ? ' has-offline-mic' : ''}`}>
              <button
                className={`pms-ctrl-btn${connectionLost ? ' is-offline' : ''}`}
                type="button"
                aria-label={connectionLost ? 'Microphone offline — reconnecting' : effectivelyMuted ? 'Unmute mic' : 'Mute mic'}
                data-muted={connectionLost || effectivelyMuted ? 'true' : undefined}
                // Offline is a transient state the customer can't fix by clicking, so the
                // control is locked rather than left to toggle into a misleading "on".
                // `userMuted` is deliberately NOT touched: once the link is back the button
                // simply reverts to whatever the customer had chosen before the drop.
                //
                // `aria-disabled` rather than `disabled`: a truly disabled button swallows
                // pointer events in every browser, so it would never show the tooltip that
                // explains *why* it's locked, and `cursor: not-allowed` wouldn't render
                // either. The click is guarded below instead.
                disabled={isSessionPaused}
                aria-disabled={connectionLost || undefined}
                onClick={() => {
                  if (connectionLost) return;
                  setUserMuted((m) => !m);
                }}
              >
                {connectionLost && <span className="pms-mic-offline-ring" aria-hidden="true" />}
                {connectionLost || effectivelyMuted ? <IconMicOff /> : <IconMic />}
              </button>
              {connectionLost && (
                <span className="pms-mic-tip" role="tooltip">
                  Connection lost — reconnecting
                </span>
              )}
              {userMuted && !isSessionPaused && !connectionLost && (
                <div className={`pms-ptt-hint${isPushToTalk ? ' is-active' : ''}`}>
                  <kbd>Space</kbd>
                  <span>{isPushToTalk ? 'Speaking…' : 'Hold to talk'}</span>
                </div>
              )}
            </div>
            <button
              className={`pms-pause-btn${isSessionPaused ? ' is-paused' : ''}`}
              type="button"
              aria-label={isSessionPaused ? 'Resume session' : 'Pause session'}
              onClick={isSessionPaused ? resumeSession : pauseSession}
            >
              {isSessionPaused ? <IconPlay /> : <IconPause />}
              {isSessionPaused ? 'Resume' : 'Pause'}
            </button>
            <button className="pms-end" type="button" onClick={endSession}>
              <IconEndCall />
              End
            </button>
          </>
        )}
        {/* Kept outside the sessionEnded gate — a fullscreen viewer needs a way back
            out even after the presentation has ended. */}
        <button
          className="pms-ctrl-btn"
          type="button"
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
        </button>
      </div>

      {sessionEnded && (
        <div className="pms-end-overlay">
          <div className="pms-end-card">
            <div className="pms-end-badge" aria-hidden="true">
              ✓
            </div>
            <h2>Presentation complete</h2>
            <p>Thanks for joining "{sessionTitle}".</p>
            {(reportActionUrl || actionUrl) && (
              <a className="pms-end-cta" href={reportActionUrl || actionUrl} target="_blank" rel="noreferrer">
                {reportActionUrl ? reportActionLabel : actionLabel}
              </a>
            )}

            <div className="pms-end-feedback">
              <span className="pms-end-feedback-label">How was this presentation?</span>
              <div className="pms-end-stars" role="radiogroup" aria-label="Rate this presentation">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="pms-end-star"
                    data-filled={(feedbackHoverRating || feedbackRating) >= star ? 'true' : undefined}
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    aria-pressed={feedbackRating === star}
                    onMouseEnter={() => setFeedbackHoverRating(star)}
                    onMouseLeave={() => setFeedbackHoverRating(0)}
                    onClick={() => setFeedbackRating(star)}
                  >
                    ★
                  </button>
                ))}
              </div>
              {feedbackRating > 0 && (
                <>
                  <textarea
                    className="pms-end-feedback-input"
                    placeholder="Anything you'd like to add? (optional)"
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                    rows={2}
                  />
                  <button
                    className="pms-end-feedback-submit"
                    type="button"
                    onClick={submitFeedback}
                    disabled={feedbackStatus === 'submitting'}
                  >
                    {feedbackStatus === 'submitting' ? 'Sending…' : feedbackStatus === 'error' ? 'Try again' : 'Send feedback'}
                  </button>
                  {feedbackStatus === 'error' && <span className="pms-end-feedback-error">Couldn't send feedback. Please try again.</span>}
                </>
              )}
              {/* Both this and a successful submit leave for the standalone thank-you
                  route — the session is over either way, so nothing stays behind. */}
              <button
                className="pms-end-feedback-skip"
                type="button"
                onClick={() => leaveToThankYou(false)}
                disabled={feedbackStatus === 'submitting'}
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}

      {isSessionPaused && !sessionEnded && (
        <div className="pms-pause-overlay" role="dialog" aria-label="Session paused" aria-live="polite">
          <div className="pms-pause-card">
            <div className="pms-pause-icon-wrap" aria-hidden="true">
              <span className="pms-pause-ring pms-pause-ring--1" />
              <span className="pms-pause-ring pms-pause-ring--2" />
              <span className="pms-pause-icon">
                <IconPause />
              </span>
            </div>
            <h2>Session paused</h2>
            <p>The narration, mic, and timer are all on hold. Resume whenever you're ready — right where you left off.</p>
            <button className="pms-pause-resume-btn" type="button" onClick={resumeSession}>
              <IconPlay />
              Resume session
            </button>
            <span className="pms-pause-elapsed">{formatTime(elapsed)} elapsed</span>
          </div>
        </div>
      )}
    </main>
  );
}
